import { BlockBlobClient } from '@azure/storage-blob'
import { EDocumentStatus, EFinancingProgram } from '@if/core/types'
import { convertFileToArrayBuffer } from '@if/core/utils'
import { useSetBusinessErrors } from '@src/data/selectors/app-config-selectors'
import { getApiClient } from '@src/services/api-client'
import {
  MutationFunction,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryObserverResult,
} from '@tanstack/react-query'
import download from 'downloadjs'
import { BusinessError } from '../../types/BusinessError'
import { Constants, DocumentDecision, FullCreditApplication } from '../../types'
import { Document, DocumentPage, DocumentRequired } from '../../types/DocumentSchema'
import { DocumentTransformationResult } from '../../types/DocumentTransformationResult'
import { fullCreditApplicationKeys } from '../full-credit-application/full-credit-application-api'
import { buildUrlWithIds } from '../make-api'

// ==================== CONSTANTS ====================

const BASE_URL = '/Applications'
const MAX_FILE_SIZE = 20971520 // 20MB

// ==================== TYPES ====================

interface DocumentBaseParams {
  creditApplicationId: string
  financingProgramId: EFinancingProgram
}

interface CreateRequiredDocumentsParams extends DocumentBaseParams {
  data: DocumentRequired[]
}

interface UpdateRequiredDocumentParams {
  data: DocumentRequired
}

interface DeleteRequiredDocumentParams {
  data: DocumentRequired
  financingProgramId: EFinancingProgram
}

interface SendDocumentParams extends DocumentBaseParams {
  documents: Document[]
  files: File[]
}

interface SendPrivateDocumentParams {
  formData: FormData
}

interface UpdatePrivateDocumentParams {
  formData: FormData
  creditApplicationId: string
  document: Document
}

interface DeletePrivateDocumentParams {
  document: Document
}

interface ApplyTransformationParams extends DocumentBaseParams {
  documents: Document[]
}

interface QuickMergeParams extends DocumentBaseParams {
  documentIds: string[]
}

interface MakeDecisionsParams extends DocumentBaseParams {
  decisions: DocumentDecision[]
}

interface DownloadDocumentParams extends DocumentBaseParams {
  documentId: string
}

// ==================== QUERY KEYS ====================

const SCOPE = 'document'
const LIST = 'list'
const PAGES = 'pages'
const REQUIRED_LIST = 'requiredList'

export const documentKeys = {
  all: [SCOPE] as const,
  list: (creditApplicationId: string, financingProgramId: EFinancingProgram) =>
    [SCOPE, LIST, creditApplicationId, financingProgramId] as const,
  pages: (creditApplicationId: string, financingProgramId: EFinancingProgram) =>
    [SCOPE, PAGES, creditApplicationId, financingProgramId] as const,
  requiredList: (creditApplicationId: string, financingProgramId: EFinancingProgram) =>
    [SCOPE, REQUIRED_LIST, creditApplicationId, financingProgramId] as const,
}

// ==================== API FUNCTIONS ====================

// GET: Document List
const getDocumentList = async (creditApplicationId: string, financingProgramId: EFinancingProgram) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<Document[]>(`${BASE_URL}/${financingProgramId}/${creditApplicationId}/Documents`)
  return response.data
}

// GET: Document Pages
const getDocumentPages = async (creditApplicationId: string, financingProgramId: EFinancingProgram) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<DocumentPage[]>(
    `${BASE_URL}/${financingProgramId}/${creditApplicationId}/Documents/Pages`,
  )
  return response.data
}

// GET: Required Documents List
const getRequiredDocumentList = async (creditApplicationId: string, financingProgramId: EFinancingProgram) => {
  const apiClient = getApiClient()
  const response = await apiClient.get<DocumentRequired[]>(
    `${BASE_URL}/${financingProgramId}/${creditApplicationId}/RequiredDocuments`,
  )
  return response.data
}

// POST: Create Required Documents
const createRequiredDocuments: MutationFunction<DocumentRequired[], CreateRequiredDocumentsParams> = async ({
  data,
  creditApplicationId,
  financingProgramId,
}) => {
  const apiClient = getApiClient()
  const response = await apiClient.post<DocumentRequired[]>(
    `${BASE_URL}/${financingProgramId}/${creditApplicationId}/RequiredDocuments`,
    data,
  )
  return response.data
}

// PUT: Update Required Document
const updateRequiredDocument: MutationFunction<DocumentRequired, UpdateRequiredDocumentParams> = async ({ data }) => {
  const apiClient = getApiClient()
  const response = await apiClient.put<DocumentRequired>(
    `${BASE_URL}/${data.financingProgramId}/${data.creditApplicationId}/RequiredDocuments/${data.id}`,
    data,
  )
  return response.data
}

// DELETE: Delete Required Document
const deleteRequiredDocument: MutationFunction<void, DeleteRequiredDocumentParams> = async ({
  data,
  financingProgramId,
}) => {
  const apiClient = getApiClient()
  await apiClient.delete(`${BASE_URL}/${financingProgramId}/${data.creditApplicationId}/RequiredDocuments/${data.id}`, {
    data,
  })
}

// POST: Send Documents (with Azure Blob upload)
// Factory function that creates the mutation function with error setter
const createSendDocuments = (
  setBusinessErrors: (errors: BusinessError[]) => void,
): MutationFunction<Document[], SendDocumentParams> => {
  return async ({ documents, files, creditApplicationId, financingProgramId }) => {
    // Check file sizes before uploading
    const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE)
    const unsupportedFiles = files.filter((file) => {
      const fileExtension = file.name.split('.').pop()?.toLowerCase() ?? ''
      const isExtensionSupported = Constants.SupportedDocumentTypes.includes(fileExtension)
      return !isExtensionSupported
    })

    if (oversizedFiles.length > 0 || unsupportedFiles.length > 0) {
      const errors: BusinessError[] = []

      if (oversizedFiles.length > 0) errors.push({ message: 'document.fileSizeTooBig' })

      if (unsupportedFiles.length > 0)
        errors.push({
          message: 'document.unsupportedFileType',
          params: { supportedTypes: Constants.SupportedDocumentTypes },
        })

      setBusinessErrors(errors)
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }, 100)
      return []
    }

    const apiClient = getApiClient()
    const response = await apiClient.post<Document[]>(
      `${BASE_URL}/${financingProgramId}/${creditApplicationId}/Documents/GenerateSasTokenUrl`,
      documents,
    )

    const ret = response.data

    // Upload files to Azure Blob
    files.forEach(async (file, index) => {
      await convertFileToArrayBuffer(file).then((fileArrayBuffer) => {
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        if (fileArrayBuffer === null || fileArrayBuffer.byteLength < 1) return Promise.reject()

        const concernedFileSASUrl = ret.find((doc) => doc.fileName === `${index}-${file.name}`)?.sasTokenUrl
        const blockBlobClient = new BlockBlobClient(concernedFileSASUrl!)
        return blockBlobClient.uploadData(fileArrayBuffer)
      })
    })

    return ret
  }
}

// POST: Send Private Document
const sendPrivateDocument: MutationFunction<Document, SendPrivateDocumentParams> = async ({ formData }) => {
  const doc: Document = JSON.parse(formData.get('payload') as string) as Document
  const apiClient = getApiClient()
  const response = await apiClient.post<Document>(
    `${BASE_URL}/${doc.financingProgramId}/${doc.creditApplicationId}/PrivateDocuments`,
    formData,
  )
  return response.data
}

// PUT: Update Private Document
const updatePrivateDocument: MutationFunction<Document, UpdatePrivateDocumentParams> = async ({
  formData,
  creditApplicationId,
  document,
}) => {
  const apiClient = getApiClient()
  const response = await apiClient.put<Document>(
    `${BASE_URL}/${document.financingProgramId}/${creditApplicationId}/PrivateDocuments/${document.id}`,
    formData,
  )
  return response.data
}

// DELETE: Delete Private Document
const deletePrivateDocument: MutationFunction<void, DeletePrivateDocumentParams> = async ({ document }) => {
  const apiClient = getApiClient()
  await apiClient.delete(
    `${BASE_URL}/${document.financingProgramId}/${document.creditApplicationId}/PrivateDocuments/${document.id}`,
    { data: document },
  )
}

// POST: Apply Document Transformation
const applyTransformation: MutationFunction<DocumentTransformationResult, ApplyTransformationParams> = async ({
  documents,
  creditApplicationId,
  financingProgramId,
}) => {
  const apiClient = getApiClient()
  const response = await apiClient.post<DocumentTransformationResult>(
    `${BASE_URL}/${financingProgramId}/${creditApplicationId}/Documents/ApplyTransformation`,
    documents,
  )
  return response.data
}

// POST: Quick Merge
const quickMerge: MutationFunction<DocumentTransformationResult, QuickMergeParams> = async ({
  documentIds,
  creditApplicationId,
  financingProgramId,
}) => {
  const apiClient = getApiClient()
  const response = await apiClient.post<DocumentTransformationResult>(
    `${BASE_URL}/${financingProgramId}/${creditApplicationId}/Documents/quickMerge`,
    documentIds,
  )
  return response.data
}

// POST: Make Decisions
const makeDecisions: MutationFunction<DocumentTransformationResult, MakeDecisionsParams> = async ({
  decisions,
  creditApplicationId,
  financingProgramId,
}) => {
  const apiClient = getApiClient()
  const response = await apiClient.post<DocumentTransformationResult>(
    `${BASE_URL}/${financingProgramId}/${creditApplicationId}/Documents/Decisions`,
    decisions,
  )
  return response.data
}

// POST: Download Document
const downloadDocument: MutationFunction<void, DownloadDocumentParams> = async ({
  documentId,
  creditApplicationId,
  financingProgramId,
}) => {
  const apiClient = getApiClient()
  const response = await apiClient.post(
    `${BASE_URL}/${financingProgramId}/${creditApplicationId}/Documents/${documentId}/Download`,
    {},
    { responseType: 'blob' },
  )

  const contentDisposition = response.headers['content-disposition'] as string | undefined
  const filenameHeader = contentDisposition?.split(';').find((n: string) => n.includes('filename='))
  if (filenameHeader) {
    const filename = filenameHeader.replace('filename=', '').trim()
    download(response.data as Blob, filename)
  }
}

// ==================== REACT QUERY HOOKS ====================

// GET: Document List
export function useGetDocumentList(
  creditApplicationId: string,
  financingProgramId: EFinancingProgram,
): [Document[], boolean, () => Promise<QueryObserverResult<Document[], Error>>] {
  const { data, isFetching, refetch } = useQuery({
    queryKey: documentKeys.list(creditApplicationId, financingProgramId),
    queryFn: () => getDocumentList(creditApplicationId, financingProgramId),
    enabled: !!creditApplicationId && !!financingProgramId,
  })

  return [data ?? [], isFetching, refetch]
}

// GET: Document Pages
export function useGetDocumentPages(
  creditApplicationId: string | undefined,
  financingProgramId: EFinancingProgram | undefined,
): [DocumentPage[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: documentKeys.pages(creditApplicationId ?? '', financingProgramId ?? ('' as EFinancingProgram)),
    queryFn: () => getDocumentPages(creditApplicationId!, financingProgramId!),
    enabled: !!creditApplicationId && !!financingProgramId,
  })

  return [data ?? [], isFetching]
}

// GET: Required Documents List
export function useGetRequiredDocumentList(
  creditApplicationId: string,
  financingProgramId: EFinancingProgram,
): [DocumentRequired[], boolean, () => Promise<QueryObserverResult<DocumentRequired[], Error>>] {
  const { data, isFetching, refetch } = useQuery({
    queryKey: documentKeys.requiredList(creditApplicationId, financingProgramId),
    queryFn: () => getRequiredDocumentList(creditApplicationId, financingProgramId),
    enabled: !!creditApplicationId && !!financingProgramId,
  })

  return [data ?? [], isFetching, refetch]
}

// POST: Create Required Documents
export function useCreateRequiredDocuments(): [
  MutationFunction<DocumentRequired[], CreateRequiredDocumentsParams>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: createRequiredDocuments,
    onSuccess: (newRequiredDocs, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId, params.financingProgramId)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          requiredDocuments: [...currentData.requiredDocuments, ...newRequiredDocs],
        })
      }
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

// PUT: Update Required Document
export function useUpdateRequiredDocument(): [
  MutationFunction<DocumentRequired, UpdateRequiredDocumentParams>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: updateRequiredDocument,
    onSuccess: (updatedRequiredDoc, params) => {
      const queryKey = fullCreditApplicationKeys.detail(
        params.data.creditApplicationId,
        params.data.financingProgramId as EFinancingProgram,
      )
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          requiredDocuments: currentData.requiredDocuments.map((rd) =>
            rd.id === updatedRequiredDoc.id ? updatedRequiredDoc : rd,
          ),
        })
      }
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

// DELETE: Delete Required Document
export function useDeleteRequiredDocument(): [
  MutationFunction<void, DeleteRequiredDocumentParams>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: deleteRequiredDocument,
    onSuccess: (_, params) => {
      const queryKey = fullCreditApplicationKeys.detail(
        params.data.creditApplicationId,
        params.financingProgramId,
      )
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          requiredDocuments: currentData.requiredDocuments.filter((rd) => rd.id !== params.data.id),
        })
      }
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

// POST: Send Documents
export function useSendDocuments(): [MutationFunction<Document[], SendDocumentParams>, boolean, boolean, () => void] {
  const queryClient = useQueryClient()
  const setBusinessErrors = useSetBusinessErrors()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: createSendDocuments(setBusinessErrors),
    onSuccess: (uploadedDocs, params) => {
      setBusinessErrors([])
      // Optimistically update the fullCreditApplication cache with new documents (status: AwaitingScan)
      // The caller (documentList) handles polling to get the actual status from the server
      if (uploadedDocs.length > 0) {
        const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId, params.financingProgramId)
        const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
        if (currentData) {
          const newDocs = uploadedDocs.map((doc) => ({ ...doc, status: EDocumentStatus.AwaitingScan }))
          queryClient.setQueryData<FullCreditApplication>(queryKey, {
            ...currentData,
            documents: [...currentData.documents, ...newDocs],
          })
        }

        // Invalidate document pages cache to refetch with new pages
        const pagesQueryKey = documentKeys.pages(params.creditApplicationId, params.financingProgramId)
        void queryClient.invalidateQueries({ queryKey: pagesQueryKey })
      }
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

// POST: Send Private Document
export function useSendPrivateDocument(): [
  MutationFunction<Document, SendPrivateDocumentParams>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: sendPrivateDocument,
    onSuccess: (newDoc, params) => {
      // Parse the document from formData to get creditApplicationId and financingProgramId
      const docFromForm: Document = JSON.parse(params.formData.get('payload') as string) as Document
      const queryKey = fullCreditApplicationKeys.detail(
        docFromForm.creditApplicationId,
        docFromForm.financingProgramId,
      )
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          documents: [...currentData.documents, newDoc],
        })
      }

      // Invalidate document pages cache to refetch with new pages
      const pagesQueryKey = documentKeys.pages(docFromForm.creditApplicationId, docFromForm.financingProgramId)
      void queryClient.invalidateQueries({ queryKey: pagesQueryKey })
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

// PUT: Update Private Document
export function useUpdatePrivateDocument(): [
  MutationFunction<Document, UpdatePrivateDocumentParams>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: updatePrivateDocument,
    onSuccess: (updatedDoc, params) => {
      const queryKey = fullCreditApplicationKeys.detail(
        params.creditApplicationId,
        params.document.financingProgramId,
      )
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          documents: currentData.documents.map((d) => (d.id === updatedDoc.id ? updatedDoc : d)),
        })
      }

      // Invalidate document pages cache to refetch with updated pages
      const pagesQueryKey = documentKeys.pages(params.creditApplicationId, params.document.financingProgramId)
      void queryClient.invalidateQueries({ queryKey: pagesQueryKey })
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

// DELETE: Delete Private Document
export function useDeletePrivateDocument(): [
  MutationFunction<void, DeletePrivateDocumentParams>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: deletePrivateDocument,
    onSuccess: (_, params) => {
      const queryKey = fullCreditApplicationKeys.detail(
        params.document.creditApplicationId,
        params.document.financingProgramId,
      )
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          documents: currentData.documents.filter((d) => d.id !== params.document.id),
        })
      }

      // Invalidate document pages cache to refetch (deleted document pages removed)
      const pagesQueryKey = documentKeys.pages(params.document.creditApplicationId, params.document.financingProgramId)
      void queryClient.invalidateQueries({ queryKey: pagesQueryKey })
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

// POST: Apply Document Transformation
export function useApplyTransformation(): [
  MutationFunction<DocumentTransformationResult, ApplyTransformationParams>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: applyTransformation,
    onSuccess: (result, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId, params.financingProgramId)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          documents: result.documents,
        })
      }

      // Also update the document pages cache
      const pagesQueryKey = documentKeys.pages(params.creditApplicationId, params.financingProgramId)
      queryClient.setQueryData<DocumentPage[]>(pagesQueryKey, result.pages)
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

// POST: Quick Merge
export function useQuickMerge(): [
  MutationFunction<DocumentTransformationResult, QuickMergeParams>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: quickMerge,
    onSuccess: (result, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId, params.financingProgramId)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          documents: result.documents,
        })
      }

      // Also update the document pages cache
      const pagesQueryKey = documentKeys.pages(params.creditApplicationId, params.financingProgramId)
      queryClient.setQueryData<DocumentPage[]>(pagesQueryKey, result.pages)
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

// POST: Make Decisions
export function useMakeDecisions(): [
  MutationFunction<DocumentTransformationResult, MakeDecisionsParams>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: makeDecisions,
    onSuccess: (result, params) => {
      const queryKey = fullCreditApplicationKeys.detail(params.creditApplicationId, params.financingProgramId)
      const currentData = queryClient.getQueryData<FullCreditApplication>(queryKey)
      if (currentData) {
        queryClient.setQueryData<FullCreditApplication>(queryKey, {
          ...currentData,
          documents: result.documents,
        })
      }

      // Also update the document pages cache
      const pagesQueryKey = documentKeys.pages(params.creditApplicationId, params.financingProgramId)
      queryClient.setQueryData<DocumentPage[]>(pagesQueryKey, result.pages)
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

// POST: Download Document
export function useDownloadDocument(): [MutationFunction<void, DownloadDocumentParams>, boolean, boolean, () => void] {
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: downloadDocument,
  })

  return [mutateAsync, isPending, isError, reset]
}

export const useAddFCFNote = (financingProgramId: EFinancingProgram, creditApplicationId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: { note: string }): Promise<void> => {
      const apiClient = getApiClient()
      const fcfNoteUrl = buildUrlWithIds(
        `/Applications/{financingProgramId}/{creditApplicationId}/Documents/FCF/AddNote`,
        {
          creditApplicationId,
          financingProgramId,
        },
      )

      await apiClient.post(fcfNoteUrl, JSON.stringify(params.note), {
        headers: { 'Content-Type': 'application/json' },
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: fullCreditApplicationKeys.detail(creditApplicationId, financingProgramId),
      })
    },
  })
}
